<template>
  <v-dialog :value="modalStatus" max-width="980" scrollable @input="closeModal">
    <v-card>
      <!-- Header -->
      <v-card-title class="modal-header pa-5">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-bell-cog-outline</v-icon>
          <div>
            <div class="text-h6 font-weight-medium">Notificaciones por proyecto</div>
            <div class="text-caption grey--text text--darken-1">
              Configura qué notificaciones quieres recibir en cada proyecto
            </div>
          </div>
        </div>
        <v-spacer />
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              small
              :outlined="!allSelected"
              :color="allSelected ? 'primary' : 'grey darken-1'"
              class="mr-3 select-all-btn"
              v-bind="attrs"
              v-on="on"
              @click="toggleAll"
            >
              <v-icon left small>{{ allSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}</v-icon>
              {{ allSelected ? 'Deseleccionar todo' : 'Seleccionar todo' }}
            </v-btn>
          </template>
          <span>{{ allSelected ? 'Quitar todas las notificaciones de todos los proyectos' : 'Activar todas las notificaciones de todos los proyectos' }}</span>
        </v-tooltip>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Column legend -->
      <div class="legend-bar px-5 py-3">
        <div class="notif-grid">
          <div class="col-project" />
          <div
            v-for="notif in notifications"
            :key="notif.key"
            class="col-notif"
          >
            <v-tooltip bottom max-width="350">
              <template #activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="d-flex flex-column align-center type-header"
                  @click="toggleColumn(notif.key)"
                >
                  <v-icon small :color="notif.color" class="mb-1">{{ notif.icon }}</v-icon>
                  <span class="notif-label" :style="{ color: notif.textColor }">
                    {{ notif.shortLabel }}
                  </span>
                  <v-icon x-small class="mt-1" :color="notif.color">
                    {{ isColumnSelected(notif.key) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
                  </v-icon>
                </div>
              </template>
              <span>
                {{ isColumnSelected(notif.key) ? 'Quitar' : 'Seleccionar' }} "{{ notif.label }}" en todos los proyectos
              </span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <v-divider />

      <!-- Project rows -->
      <v-card-text class="pa-0" style="max-height: 460px; overflow-y: auto;">
        <div
          v-for="(project, index) in config"
          :key="project.project_id"
          class="project-row px-5"
          :class="{ 'row-alt': index % 2 === 1, 'row-error': hasSpecialtyError(project) }"
        >
          <div class="notif-grid align-center">
            <div class="col-project d-flex align-center">
              <v-tooltip right>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="mr-2 flex-shrink-0"
                    :color="isRowSelected(project) ? 'primary' : 'grey lighten-1'"
                    v-bind="attrs"
                    v-on="on"
                    @click="toggleRow(project)"
                  >
                    <v-icon x-small>
                      {{ isRowSelected(project) ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ isRowSelected(project) ? 'Quitar todas' : 'Seleccionar todas' }} las notificaciones de {{ project.name }}</span>
              </v-tooltip>
              <div class="d-flex flex-column" style="min-width:0">
                <span class="text-body-2 font-weight-medium text-truncate">{{ project.name }}</span>
                <span class="text-caption grey--text text-truncate">{{ project.companyName }}</span>
              </div>
            </div>

            <div
              v-for="notif in notifications"
              :key="notif.key"
              class="col-notif d-flex justify-center align-center"
            >
              <!-- Columna especial: rdi_interaction_emails + selector de especialidades -->
              <template v-if="notif.key === 'rdi_interaction_emails'">
                <div class="d-flex flex-column align-center">
                  <v-checkbox
                    v-model="project[notif.key]"
                    :color="notif.color"
                    dense
                    hide-details
                    class="ma-0 pa-0 checkbox-center"
                    @change="onRdiCheckChange(project)"
                  />

                  <!--
                    IMPORTANTE: el activator siempre está montado en el DOM (usamos v-show,
                    no v-if, dentro de él). Si se usa v-if aquí, Vuetify calcula la posición
                    del menú ANTES de que el botón exista/se haya renderizado con su tamaño
                    final, y el menú termina posicionado en la esquina superior derecha por
                    defecto. Con v-show el nodo ya existe en el DOM (solo oculto), así que la
                    posición se calcula correctamente ni bien se vuelve visible.
                  -->
                  <v-menu
                    v-model="project._specialtyMenuOpen"
                    :close-on-content-click="false"
                    :close-on-click="false"
                    offset-y
                    min-width="320"
                    max-width="340"
                  >
                    <template #activator="{ attrs }">
                      <v-btn
                        v-show="project.rdi_interaction_emails"
                        text
                        x-small
                        :color="hasSpecialtyError(project) && !project._specialtiesLoading ? 'error' : 'primary'"
                        class="specialty-trigger mt-1"
                        v-bind="attrs"
                        @click="openSpecialtyMenu(project)"
                      >
                        <v-icon left x-small>{{ project._specialtiesLoading ? 'mdi-loading mdi-spin' : 'mdi-tag-outline' }}</v-icon>
                        <span>{{ specialtyTriggerLabel(project) }}</span>
                        <v-icon right x-small>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>

                    <v-card v-if="project._specialtyMenuOpen" class="specialty-menu-card">
                      <v-card-title class="specialty-menu-header pa-3">
                        <div class="d-flex flex-column" style="min-width:0">
                          <span class="text-body-2 font-weight-medium">Especialidades</span>
                          <span class="text-caption grey--text text-truncate">{{ project.name }}</span>
                        </div>
                        <v-spacer />
                        <v-btn icon x-small @click="cancelSpecialties(project)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>

                      <v-divider />

                      <div v-if="project._specialtiesLoading" class="pa-6 text-center">
                        <v-progress-circular indeterminate color="primary" size="24" />
                      </div>

                      <template v-else>
                        <div
                          v-if="project._specialtiesCatalog.length > 6"
                          class="px-3 pt-2"
                        >
                          <v-text-field
                            v-model="project._specialtySearch"
                            placeholder="Buscar especialidad..."
                            dense
                            hide-details
                            outlined
                            prepend-inner-icon="mdi-magnify"
                            clearable
                          />
                        </div>

                        <div class="d-flex align-center justify-space-between px-3 pt-2">
                          <span class="text-caption grey--text">
                            {{ project._specialtyDraft.length }} de {{ project._specialtiesCatalog.length }} seleccionadas
                          </span>
                          <v-btn
                            v-if="project._specialtiesCatalog.length"
                            x-small
                            text
                            color="primary"
                            class="text-none"
                            @click="toggleAllSpecialties(project)"
                          >
                            {{ isAllSpecialtiesSelected(project) ? 'Ninguna' : 'Todas' }}
                          </v-btn>
                        </div>

                        <v-card-text class="pa-0 pt-1" style="max-height: 240px; overflow-y: auto;">
                          <v-checkbox
                            v-for="s in filteredSpecialties(project)"
                            :key="s.id"
                            v-model="project._specialtyDraft"
                            :value="s.id"
                            :label="s.name"
                            color="#5C6BC0"
                            dense
                            hide-details
                            class="ma-0 px-3 py-1 specialty-checkbox"
                          />
                          <div
                            v-if="!filteredSpecialties(project).length"
                            class="pa-4 text-center grey--text text-caption"
                          >
                            {{ project._specialtiesCatalog.length ? 'Sin resultados' : 'No hay especialidades configuradas en este proyecto' }}
                          </div>
                        </v-card-text>

                        <v-divider />

                        <div
                          v-if="project._specialtyDraft.length === 0"
                          class="px-3 pt-2 text-caption grey--text d-flex align-center"
                        >
                          <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                          Sin especialidades, se desactivará el email de RDI
                        </div>

                        <v-card-actions class="pa-3">
                          <v-spacer />
                          <v-btn text x-small class="text-none" @click="cancelSpecialties(project)">
                            Cancelar
                          </v-btn>
                          <v-btn
                            color="primary"
                            x-small
                            elevation="0"
                            class="text-none"
                            @click="applySpecialties(project)"
                          >
                            Aplicar
                          </v-btn>
                        </v-card-actions>
                      </template>
                    </v-card>
                  </v-menu>
                </div>
              </template>

              <v-checkbox
                v-else
                v-model="project[notif.key]"
                :color="notif.color"
                dense
                hide-details
                class="ma-0 pa-0 checkbox-center"
              />
            </div>
          </div>
        </div>

        <div v-if="!config.length" class="pa-8 text-center grey--text">
          No hay proyectos disponibles
        </div>
      </v-card-text>

      <v-divider />

      <!-- Footer -->
      <v-card-actions class="pa-4">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" :color="hasAnyError ? 'error' : 'grey darken-1'">
            {{ hasAnyError ? 'mdi-alert-circle-outline' : 'mdi-information-outline' }}
          </v-icon>
          <span class="text-caption" :class="hasAnyError ? 'error--text' : 'grey--text'">
            {{ hasAnyError
              ? 'Hay proyectos con email de RDI activado sin especialidades seleccionadas'
              : 'Los cambios aplican de forma inmediata por proyecto' }}
          </span>
        </div>
        <v-spacer />
        <v-btn color="primary" elevation="0" :disabled="hasAnyError" @click="save">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Guardar cambios
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { setUserProjectNotificationsConfig } from '@/helpers/api/general'
import { getProjectSpecialties } from '@/helpers/api/project'

const NOTIF_KEYS = [
  'panel_notifications',
  'tasks_interaction_emails',
  'rdi_interaction_emails',
  'additionals_interaction_emails',
  'daily_reports',
]

export default {
  name: 'NotificationsConfigModal',
  props: {
    modalStatus: { type: Boolean, required: true },
    closeModal:  { type: Function, required: true },
  },
  computed: {
    projectsList() {
      return this.$store.projectsStore.state.projectsList
    },
    allSelected() {
      return this.config.length > 0 &&
        this.config.every(p => NOTIF_KEYS.every(k => p[k]))
    },
    hasAnyError() {
      return this.config.some(p => this.hasSpecialtyError(p))
    },
  },
  data() {
    return {
      config: [],
      notifications: [
        { key: 'panel_notifications', label: 'Notificaciones en Panel', shortLabel: 'Panel', icon: 'mdi-bell-outline', color: '#5C6BC0', textColor: '#5C6BC0' },
        { key: 'tasks_interaction_emails', label: 'Email por Interacción en Tareas', shortLabel: 'Email\nTareas', icon: 'mdi-email-outline', color: '#26A69A', textColor: '#00695C' },
        { key: 'rdi_interaction_emails', label: 'Email por Interacción en RDI', shortLabel: 'Email\nRDI', icon: 'mdi-email-outline', color: '#26A69A', textColor: '#00695C' },
        { key: 'additionals_interaction_emails', label: 'Email por Interacción en Adicionales', shortLabel: 'Email\nAdicionales', icon: 'mdi-email-outline', color: '#26A69A', textColor: '#00695C' },
        { key: 'daily_reports', label: 'Resumen Diario de Tareas y RDI', shortLabel: 'Resumen\nDiario Tareas / RDI', icon: 'mdi-calendar-today-outline', color: '#FFA726', textColor: '#E65100' },
      ],
    }
  },
  watch: {
    modalStatus(val) {
      if (val) this.init()
    },
  },
  mounted() {
    if (this.modalStatus) this.init()
  },
  methods: {
    init() {
      this.config = (this.projectsList || []).map(p => {
        const cfg = p.user_notification_config || {}
        const subscriptions = p.user_specialty_subscription || []
        return {
          project_id:                     p.id,
          name:                           p.name,
          companyName:                    p.company?.name || '',
          panel_notifications:            cfg.panel_notifications            ?? false,
          tasks_interaction_emails:       cfg.tasks_interaction_emails       ?? false,
          rdi_interaction_emails:         cfg.rdi_interaction_emails         ?? false,
          additionals_interaction_emails: cfg.additionals_interaction_emails ?? false,
          daily_reports:                  cfg.daily_reports                  ?? false,
          specialty_ids:                  subscriptions.map(s => s.specialty_id),
          // estado interno de UI, no se envía al backend
          _specialtyMenuOpen:             false,
          _specialtiesLoading:            false,
          _specialtiesCatalog:            [],
          _specialtiesLoaded:             false,
          _specialtyDraft:                [],
          _specialtySearch:               '',
        }
      })

      // Precargamos el catálogo de especialidades solo para los proyectos que ya
      // tienen el check de RDI activo, para que el trigger muestre el label real
      // ("Todas" / "N especialidades") desde el primer render, en vez de quedar
      // atascado en "Cargando..." hasta que el usuario haga click.
      this.config
        .filter(p => p.rdi_interaction_emails)
        .forEach(p => this.loadSpecialties(p))
    },
    async loadSpecialties(project) {
      if (project._specialtiesLoaded) return
      project._specialtiesLoading = true
      try {
        const res = await getProjectSpecialties({ project_id: project.project_id })
        project._specialtiesCatalog = res.specialties || res || []
        project._specialtiesLoaded = true
      } catch (e) {
        project._specialtiesCatalog = []
      } finally {
        project._specialtiesLoading = false
      }
    },
    async openSpecialtyMenu(project) {
      await this.loadSpecialties(project)
      project._specialtyDraft = [...project.specialty_ids]
      project._specialtySearch = ''
      // Esperamos a que el DOM refleje el trigger visible (v-show) antes de
      // abrir el menú, para que Vuetify calcule la posición sobre el elemento
      // ya con su tamaño/posición final.
      await this.$nextTick()
      project._specialtyMenuOpen = true
    },
    applySpecialties(project) {
      if (project._specialtyDraft.length === 0) {
        // No tiene sentido dejar el check global activo sin ninguna especialidad
        // seleccionada (y para proyectos sin especialidades configuradas, esta
        // es la única forma de salir del estado sin quedar bloqueado).
        project.specialty_ids = []
        project.rdi_interaction_emails = false
      } else {
        project.specialty_ids = [...project._specialtyDraft]
      }
      project._specialtyMenuOpen = false
    },
    cancelSpecialties(project) {
      project._specialtyMenuOpen = false
      // Si al cancelar no quedó nada guardado previamente (primera vez que
      // configura o simplemente no tenía nada), no dejamos el check global
      // activo sin especialidades detrás.
      if (project.specialty_ids.length === 0) {
        project.rdi_interaction_emails = false
      }
    },
    filteredSpecialties(project) {
      const q = (project._specialtySearch || '').toLowerCase().trim()
      if (!q) return project._specialtiesCatalog
      return project._specialtiesCatalog.filter(s => s.name.toLowerCase().includes(q))
    },
    isAllSpecialtiesSelected(project) {
      return project._specialtiesCatalog.length > 0 &&
        project._specialtiesCatalog.every(s => project._specialtyDraft.includes(s.id))
    },
    toggleAllSpecialties(project) {
      if (this.isAllSpecialtiesSelected(project)) {
        project._specialtyDraft = []
      } else {
        project._specialtyDraft = project._specialtiesCatalog.map(s => s.id)
      }
    },
    specialtyTriggerLabel(project) {
      if (project._specialtiesLoading || !project._specialtiesLoaded) return 'Cargando...'
      const selected = project.specialty_ids.length
      const total = project._specialtiesCatalog.length
      if (selected === 0) return 'Sin especialidades'
      if (total > 0 && selected === total) return 'Todas las especialidades'
      return `${selected} especialidad${selected > 1 ? 'es' : ''}`
    },
    hasSpecialtyError(project) {
      return project.rdi_interaction_emails && project.specialty_ids.length === 0
    },
    // openMenu = false se usa en acciones masivas (toggleAll/toggleRow/toggleColumn)
    // para no abrir múltiples menús a la vez.
    onRdiCheckChange(project, { openMenu = true } = {}) {
      if (project.rdi_interaction_emails) {
        this.loadSpecialties(project).then(() => {
          // Guard: si el usuario desmarcó el check mientras el fetch estaba en
          // curso, no pisamos ese estado más nuevo con el default de "todas".
          if (!project.rdi_interaction_emails) return
          if (project.specialty_ids.length === 0) {
            project.specialty_ids = project._specialtiesCatalog.map(s => s.id)
          }
          if (openMenu) this.openSpecialtyMenu(project)
        })
      } else {
        project.specialty_ids = []
        project._specialtyMenuOpen = false
      }
    },
    toggleAll() {
      const newValue = !this.allSelected
      this.config.forEach(p => {
        NOTIF_KEYS.forEach(k => { p[k] = newValue })
        this.onRdiCheckChange(p, { openMenu: false })
      })
    },
    isRowSelected(project) {
      return NOTIF_KEYS.every(k => project[k])
    },
    toggleRow(project) {
      const newValue = !this.isRowSelected(project)
      NOTIF_KEYS.forEach(k => { project[k] = newValue })
      this.onRdiCheckChange(project, { openMenu: false })
    },
    isColumnSelected(key) {
      return this.config.length > 0 && this.config.every(p => p[key])
    },
    toggleColumn(key) {
      const newValue = !this.isColumnSelected(key)
      this.config.forEach(p => {
        p[key] = newValue
        if (key === 'rdi_interaction_emails') this.onRdiCheckChange(p, { openMenu: false })
      })
    },
    async save() {
      if (this.hasAnyError) return
      const payload = this.config.map(p => ({
        project_id:                     p.project_id,
        panel_notifications:            p.panel_notifications,
        tasks_interaction_emails:       p.tasks_interaction_emails,
        rdi_interaction_emails:         p.rdi_interaction_emails,
        additionals_interaction_emails: p.additionals_interaction_emails,
        daily_reports:                  p.daily_reports,
        specialty_ids:                  p.specialty_ids,
      }))
      await setUserProjectNotificationsConfig({ configurations: payload })
      this.$store.projectsStore.dispatch('getUserProjects')
      this.closeModal()
    },
  },
}
</script>

<style scoped>
.modal-header { background: #FAFAFA; }
.legend-bar { background: #F5F5F5; }

.notif-grid {
  display: grid;
  grid-template-columns: 200px repeat(5, 1fr);
  align-items: center;
  width: 100%;
}

.col-project { min-width: 0; padding-right: 8px; }
.col-notif { min-width: 0; text-align: center; padding: 0 2px; }

.notif-label {
  font-size: 10px;
  line-height: 1.25;
  text-align: center;
  white-space: pre-line;
  word-break: break-word;
  display: block;
}

.type-header {
  cursor: pointer;
  border-radius: 6px;
  padding: 4px 2px;
  transition: background 0.15s;
}
.type-header:hover { background: #EEEEEE; }

.project-row {
  min-height: 52px;
  display: flex;
  align-items: center;
  transition: background 0.15s;
}
.project-row:hover { background: #F3F6FF; }
.row-alt { background: #FAFAFA; }
.row-alt:hover { background: #F3F6FF; }
.row-error { background: #FFF5F5; }
.row-error:hover { background: #FFEBEE; }

.select-all-btn { text-transform: none; letter-spacing: 0; font-size: 13px; }

.checkbox-center { display: flex !important; justify-content: center; }
.checkbox-center >>> .v-input__control { width: auto !important; }

.specialty-trigger {
  text-transform: none;
  letter-spacing: 0;
  font-size: 10px;
  min-width: 0;
  padding: 0 4px !important;
}

.specialty-menu-card {
  border-radius: 8px;
}

.specialty-menu-header {
  display: flex;
  align-items: center;
  background: #FAFAFA;
}

.specialty-checkbox >>> .v-label {
  font-size: 13px;
}

.specialty-checkbox:hover {
  background: #F3F6FF;
}
</style>